import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import {
  getDashboard,
  getRequest,
  listApprovals,
  listEvidence,
  listRequests,
  type ListRequestsParams,
  reviewAllRequests,
} from './audit';
import {
  listAgentRuns,
  getAgentRunDetail,
  type ListAgentRunsParams,
} from './agent-runs';
import { reviewRequest, updateSettings, type UpdateSettingsParams } from './settings';
import { getProjects, revealRequest } from './projects';
import { getMembers, addMember, getOrg, type AddMemberParams } from './orgs';
import { listApiKeys, createApiKey, revokeApiKey, type CreateApiKeyParams } from './apikeys';
import {
  activatePack, deactivatePack, getPacks, setPackMode,
  setPackOverride, clearPackOverride, createCustomRule, updateCustomRule, deleteCustomRule,
  getRevisions, rollbackRevision, unpinRevision,
  type OverridePatch, type CreateCustomRule,
} from './packs';

/** Data-layer hooks — screens consume these and never touch fetch/transport directly. */

// Poll on an interval so live demo runs surface without a manual reload. `enabled` lets the shell
// call it only once authenticated (avoids a guaranteed 401 on the public landing).
export const useDashboard = (enabled = true) =>
  useQuery({
    queryKey: ['dashboard'],
    queryFn: getDashboard,
    refetchInterval: 5_000,
    staleTime: 0,
    enabled,
  });

export const useAuditRequests = (params: ListRequestsParams = {}) =>
  useQuery({
    queryKey: ['audit-requests', params],
    queryFn: () => listRequests(params),
    refetchInterval: 5_000,
    staleTime: 0,
  });

// Request detail (pipeline stages + masked detections). Fetched only when a row is selected.
export const useAuditRequestDetail = (id: string | null) =>
  useQuery({
    queryKey: ['audit-request', id],
    queryFn: () => getRequest(id as string),
    enabled: !!id,
  });

// Mutations — persist operator actions, then refresh the dashboard so it re-derives from the DB.
export const useReviewRequest = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: 'acknowledged' | 'dismissed' }) =>
      reviewRequest(id, decision),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
      void qc.invalidateQueries({ queryKey: ['audit-requests'] });
      void qc.invalidateQueries({ queryKey: ['approvals'] });
    },
  });
};

// Approvals inbox (flagged requests), filterable by review status; paginated and polled.
export const useApprovals = (status = 'pending', page = 1) =>
  useQuery({
    queryKey: ['approvals', status, page],
    queryFn: () => listApprovals(status, page),
    refetchInterval: 5_000,
    staleTime: 0,
    placeholderData: (prev) => prev,
  });

// Bulk-clear every pending flagged request in one call.
export const useReviewAllRequests = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (decision: 'acknowledged' | 'dismissed') => reviewAllRequests(decision),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['approvals'] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
};

// Evidence Vault record metadata (paginated), polled.
export const useEvidence = (page = 1, pageSize = 50) =>
  useQuery({
    queryKey: ['evidence', page, pageSize],
    queryFn: () => listEvidence(page, pageSize),
    refetchInterval: 5_000,
    staleTime: 0,
  });

// Projects + per-project stats (polled, like the dashboard).
export const useProjects = (enabled = true) =>
  useQuery({
    queryKey: ['projects'],
    queryFn: getProjects,
    refetchInterval: 5_000,
    staleTime: 0,
    enabled,
  });

// Reveal a request's vaulted originals (owner/admin). Not cached — fetched on explicit click.
export const useRevealRequest = () =>
  useMutation({ mutationFn: (id: string) => revealRequest(id) });

export const useUpdateSettings = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: UpdateSettingsParams) => updateSettings(patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
};

// ── Packs: live catalog + activation (per-project) ──
export const usePacks = (projectId: string | null | undefined, enabled = true) =>
  useQuery({
    queryKey: ['packs', projectId],
    queryFn: () => getPacks(projectId as string),
    staleTime: 0,
    enabled: enabled && !!projectId,
  });

// Activation/mode changes affect the gateway on its next config-bundle pull, so invalidate the
// dashboard too (deployed-pack posture) alongside the packs list. Activation writes are per-project.
export const usePackMutations = (projectId: string | null | undefined) => {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['packs'] });
    void qc.invalidateQueries({ queryKey: ['dashboard'] });
    void qc.invalidateQueries({ queryKey: ['pack-revisions'] });
  };
  const pid = () => projectId as string;
  return {
    activate: useMutation({ mutationFn: (id: string) => activatePack(id, pid()), onSuccess: invalidate }),
    deactivate: useMutation({ mutationFn: (id: string) => deactivatePack(id, pid()), onSuccess: invalidate }),
    setMode: useMutation({
      mutationFn: (v: { id: string; mode: 'enforce' | 'monitor' }) => setPackMode(v.id, v.mode, pid()),
      onSuccess: invalidate,
    }),
    override: useMutation({
      mutationFn: (v: { id: string; patch: OverridePatch }) => setPackOverride(v.id, v.patch),
      onSuccess: invalidate,
    }),
    clearOverride: useMutation({ mutationFn: (id: string) => clearPackOverride(id), onSuccess: invalidate }),
    createRule: useMutation({
      mutationFn: (v: { id: string; rule: CreateCustomRule }) => createCustomRule(v.id, v.rule),
      onSuccess: invalidate,
    }),
    updateRule: useMutation({
      mutationFn: (v: { ruleId: string; patch: { mode?: 'enforce' | 'monitor'; enabled?: boolean } }) =>
        updateCustomRule(v.ruleId, v.patch),
      onSuccess: invalidate,
    }),
    deleteRule: useMutation({ mutationFn: (ruleId: string) => deleteCustomRule(ruleId), onSuccess: invalidate }),
    rollback: useMutation({ mutationFn: (version: string) => rollbackRevision(version), onSuccess: invalidate }),
    unpin: useMutation({ mutationFn: () => unpinRevision(), onSuccess: invalidate }),
  };
};

export const useRevisions = (enabled = true) =>
  useQuery({ queryKey: ['pack-revisions'], queryFn: getRevisions, enabled, staleTime: 0 });

// Org members — live tenancy. Keyed by orgId so switching orgs refetches.
export const useMembers = (orgId: string | null | undefined) =>
  useQuery({
    queryKey: ['org-members', orgId],
    queryFn: () => getMembers(orgId as string),
    enabled: !!orgId,
    staleTime: 30_000,
  });

export const useAddMember = (orgId: string | null | undefined) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: AddMemberParams) => addMember(orgId as string, params),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['org-members', orgId] });
    },
  });
};

// Org details (for the slug → per-org gateway vanity URL).
export const useOrg = (orgId: string | null | undefined) =>
  useQuery({
    queryKey: ['org', orgId],
    queryFn: () => getOrg(orgId as string),
    enabled: !!orgId,
    staleTime: 5 * 60_000,
  });

// API keys (gateway data-plane credentials).
export const useApiKeys = (orgId: string | null | undefined) =>
  useQuery({
    queryKey: ['api-keys', orgId],
    queryFn: () => listApiKeys(orgId as string),
    enabled: !!orgId,
    staleTime: 30_000,
  });

export const useCreateApiKey = (orgId: string | null | undefined) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: CreateApiKeyParams) => createApiKey(orgId as string, params),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['api-keys', orgId] }),
  });
};

export const useRevokeApiKey = (orgId: string | null | undefined) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => revokeApiKey(orgId as string, id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['api-keys', orgId] }),
  });
};

// ── Agent Runs ──
export const useAgentRuns = (params: ListAgentRunsParams = {}) =>
  useQuery({
    queryKey: ['agent-runs', params],
    queryFn: () => listAgentRuns(params),
    refetchInterval: 5_000,
    staleTime: 0,
  });

export const useAgentRunDetail = (id: string | null) =>
  useQuery({
    queryKey: ['agent-run', id],
    queryFn: () => getAgentRunDetail(id as string),
    enabled: !!id,
  });
